/**
 * Context Preview Component
 *
 * Shows the user what context will be captured with their submission.
 */

import { useState } from 'react';
import type { CapturedContext } from '../types';
import clsx from 'clsx';

// Chevron icon for collapsible sections
const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

interface ContextPreviewProps {
  context: CapturedContext;
  showConsoleErrors?: boolean;
  showNetworkErrors?: boolean;
  showEntities?: boolean;
}

export function ContextPreview({
  context,
  showConsoleErrors = true,
  showNetworkErrors = true,
  showEntities = true,
}: ContextPreviewProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['page']));

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const hasConsoleErrors = context.consoleErrors.length > 0;
  const hasNetworkErrors = context.networkErrors.length > 0;
  const hasEntities = context.entities.length > 0;

  return (
    <div className="psc-context-preview">
      <h4 className="psc-context-preview__title">Context Information</h4>
      <p className="psc-context-preview__description">
        This information will be included with your submission to help diagnose the issue.
      </p>

      {/* Page Context */}
      <div className="psc-context-section">
        <button
          className="psc-context-section__header"
          onClick={() => toggleSection('page')}
          aria-expanded={expandedSections.has('page')}
        >
          <ChevronIcon isOpen={expandedSections.has('page')} />
          <span>Page Context</span>
        </button>
        {expandedSections.has('page') && (
          <div className="psc-context-section__content">
            <dl className="psc-context-dl">
              <dt>Application</dt>
              <dd>{context.appName}</dd>
              <dt>Page</dt>
              <dd>{context.page.title || context.page.url}</dd>
              <dt>URL</dt>
              <dd className="psc-context-dl__code">{context.page.url}</dd>
              {context.page.module && (
                <>
                  <dt>Module</dt>
                  <dd>{context.page.module}</dd>
                </>
              )}
              {context.page.feature && (
                <>
                  <dt>Feature</dt>
                  <dd>{context.page.feature}</dd>
                </>
              )}
            </dl>
          </div>
        )}
      </div>

      {/* User Context */}
      {context.user && (
        <div className="psc-context-section">
          <button
            className="psc-context-section__header"
            onClick={() => toggleSection('user')}
            aria-expanded={expandedSections.has('user')}
          >
            <ChevronIcon isOpen={expandedSections.has('user')} />
            <span>User Context</span>
          </button>
          {expandedSections.has('user') && (
            <div className="psc-context-section__content">
              <dl className="psc-context-dl">
                <dt>User ID</dt>
                <dd>{context.user.id}</dd>
                <dt>Role</dt>
                <dd>{context.user.role}</dd>
                {context.user.name && (
                  <>
                    <dt>Name</dt>
                    <dd>{context.user.name}</dd>
                  </>
                )}
              </dl>
            </div>
          )}
        </div>
      )}

      {/* Browser Context */}
      <div className="psc-context-section">
        <button
          className="psc-context-section__header"
          onClick={() => toggleSection('browser')}
          aria-expanded={expandedSections.has('browser')}
        >
          <ChevronIcon isOpen={expandedSections.has('browser')} />
          <span>Browser Info</span>
        </button>
        {expandedSections.has('browser') && (
          <div className="psc-context-section__content">
            <dl className="psc-context-dl">
              <dt>Viewport</dt>
              <dd>
                {context.browser.viewportWidth} x {context.browser.viewportHeight}
              </dd>
              <dt>Screen</dt>
              <dd>
                {context.browser.screenWidth} x {context.browser.screenHeight}
              </dd>
              <dt>Language</dt>
              <dd>{context.browser.language}</dd>
              <dt>Timezone</dt>
              <dd>{context.browser.timezone}</dd>
              <dt>Online</dt>
              <dd>{context.browser.online ? 'Yes' : 'No'}</dd>
            </dl>
          </div>
        )}
      </div>

      {/* Console Errors */}
      {showConsoleErrors && hasConsoleErrors && (
        <div className="psc-context-section">
          <button
            className="psc-context-section__header psc-context-section__header--error"
            onClick={() => toggleSection('console')}
            aria-expanded={expandedSections.has('console')}
          >
            <ChevronIcon isOpen={expandedSections.has('console')} />
            <span>Console Errors ({context.consoleErrors.length})</span>
          </button>
          {expandedSections.has('console') && (
            <div className="psc-context-section__content">
              <ul className="psc-error-list">
                {context.consoleErrors.slice(0, 10).map((error, index) => (
                  <li key={index} className={clsx('psc-error-item', `psc-error-item--${error.type}`)}>
                    <span className="psc-error-item__type">{error.type}</span>
                    <span className="psc-error-item__message">{error.message}</span>
                    <span className="psc-error-item__time">
                      {new Date(error.timestamp).toLocaleTimeString()}
                    </span>
                  </li>
                ))}
                {context.consoleErrors.length > 10 && (
                  <li className="psc-error-list__more">
                    +{context.consoleErrors.length - 10} more errors
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Network Errors */}
      {showNetworkErrors && hasNetworkErrors && (
        <div className="psc-context-section">
          <button
            className="psc-context-section__header psc-context-section__header--error"
            onClick={() => toggleSection('network')}
            aria-expanded={expandedSections.has('network')}
          >
            <ChevronIcon isOpen={expandedSections.has('network')} />
            <span>Network Errors ({context.networkErrors.length})</span>
          </button>
          {expandedSections.has('network') && (
            <div className="psc-context-section__content">
              <ul className="psc-error-list">
                {context.networkErrors.slice(0, 5).map((error, index) => (
                  <li key={index} className="psc-error-item psc-error-item--network">
                    <span className="psc-error-item__status">{error.status}</span>
                    <span className="psc-error-item__method">{error.method}</span>
                    <span className="psc-error-item__url">{error.url}</span>
                  </li>
                ))}
                {context.networkErrors.length > 5 && (
                  <li className="psc-error-list__more">
                    +{context.networkErrors.length - 5} more errors
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Entities */}
      {showEntities && hasEntities && (
        <div className="psc-context-section">
          <button
            className="psc-context-section__header"
            onClick={() => toggleSection('entities')}
            aria-expanded={expandedSections.has('entities')}
          >
            <ChevronIcon isOpen={expandedSections.has('entities')} />
            <span>Selected Entities ({context.entities.length})</span>
          </button>
          {expandedSections.has('entities') && (
            <div className="psc-context-section__content">
              <ul className="psc-entity-list">
                {context.entities.map((entity, index) => (
                  <li key={index} className="psc-entity-item">
                    <span className="psc-entity-item__type">{entity.type}</span>
                    <span className="psc-entity-item__name">{entity.name || entity.id}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
